import { createContext, useCallback, useState } from 'react';
import { arrayWithoutElementAtIndex } from '~/app/utils/array-without-element-at-index';

export type Footnote = { key: string; value: string };

type FootnoteContextType = {
	/**
	 * Adds a footnote to the context. Keys must be unique.
	 */
	addFootnote: (key: string, value: string) => void;
	/**
	 * Registers a footnote and returns the corresponding reference.
	 *
	 * @example
	 * registerFootnote('carbon-dioxide-emissions', 'Some text...') // returns '1'
	 */
	registerFootnote: (key: string, value: string) => string;
	/**
	 * Removes a footnote.
	 */
	unregisterFootnote: (key: string) => void;
	/**
	 * replaces a footnote. Does not replace the reference.
	 */
	updateFootnote: (key: string, value: string) => void;
	/**
	 * Returns the reference (i.e., number) for the given footnote key.
	 */
	getReference: (key: string) => string;
	/**
	 * Returns all footnotes that were added to the context.
	 */
	getFootnotes: () => Footnote[];
	/**
	 * Triggers a rerender to register footnotes again.
	 * Should be called, when dialog content changes, i.e., when data is loaded.
	 *
	 * (This is a workaround. The dialog content is rerendering once data is loaded, but since footnotes are rendered outside of the dialog children, they are not rerendered.)
	 *
	 * @example
	 * // In a component that is rendered once data is loaded:
	 * const { reload } = useFootnotes();
	 * useEffect(() => {
	 *	  reload();
	 * }, [reload]);
	 */
	reload: () => void;
};

export const FootnoteContext = createContext(null as unknown as FootnoteContextType);

export function FootnoteProvider({ children }: { children?: React.ReactNode }) {
	const [, setApplyChanges] = useState(0);
	const [footnotes, setFootnotes] = useState([] as Footnote[]);

	const ctxValue: FootnoteContextType = {
		addFootnote: (key, value) => {
			if (!footnotes.find((f) => f.key === key)) {
				setFootnotes([{ key, value }].concat(footnotes.concat()));
			}
		},
		getReference: (key) => {
			const index = footnotes.findIndex((f) => f.key === key);
			return index >= 0 ? String.fromCharCode(index + 'a'.charCodeAt(0)) : '';
		},
		registerFootnote: (key, value) => {
			if (value) {
				if (!footnotes.find((f) => f.key === key)) {
					setFootnotes([{ key, value }].concat(footnotes.concat()));
					return footnotes.length.toString();
				}
				return String.fromCharCode(footnotes.findIndex((f) => f.key === key) + 'a'.charCodeAt(0));
			}
			return '';
		},
		unregisterFootnote: (key) => {
			if (!footnotes.find((f) => f.key === key)) return;
			const index = footnotes.findIndex((f) => f.key === key);

			const modifiedFootnotes = arrayWithoutElementAtIndex(footnotes, index);
			footnotes.splice(0, footnotes.length);
			/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
			modifiedFootnotes.map((f: any) => footnotes.push(f));
		},
		updateFootnote(key, value) {
			if (value) {
				if (!footnotes.find((f) => f.key === key)) return;
				const index = footnotes.findIndex((f) => f.key === key);
				footnotes[index].value = value;
			}
		},
		getFootnotes() {
			return footnotes;
		},
		reload: useCallback(() => {
			setFootnotes([]);
			setApplyChanges((n) => n + 1);
		}, []),
	};

	return <FootnoteContext.Provider value={ctxValue}>{children}</FootnoteContext.Provider>;
}
